import { useSyncExternalStore } from 'react';

/**
 * Lightweight global store for "triggered alerts" — the banner shown on the
 * dashboard and /access, and the alert indicator retained on the floating nav
 * pill once the banner is dismissed.
 *
 * This is deliberately self-contained (no context wiring) so it can be read
 * from both a page body and the Header pill. The real alert source is wired in
 * later by calling `alertStore.setAlerts(...)` from wherever alerts are
 * fetched; until then a single placeholder alert is seeded so the design is
 * visible end-to-end (banner -> dismiss -> nav-pill icon -> re-open).
 *
 * "Shows once": the dismissed state is persisted to sessionStorage, so once a
 * user closes the banner it stays collapsed to the nav-pill icon for the rest
 * of that tab session (across route changes and reloads).
 */

const DISMISS_KEY = 'projector.alertBannerDismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const writeDismissed = (value) => {
  try {
    if (value) sessionStorage.setItem(DISMISS_KEY, '1');
    else sessionStorage.removeItem(DISMISS_KEY);
  } catch (e) {
    /* sessionStorage unavailable (private mode / SSR) — fall back to memory */
  }
};

// Free-plan storage alert thresholds. The ONLY storage alert we surface is for
// free-plan users who exceed either 15GB of storage or 5 total files
// (videos + photos). Everyone else (paid plans, new/minimal accounts) sees
// nothing — no more blanket "92% full" placeholder.
const FREE_STORAGE_ALERT_GB = 15;
const FREE_STORAGE_ALERT_FILES = 5;
const STORAGE_ALERT_ID = 'free-storage-limit';

let state = {
  alerts: [],
  bannerDismissed: readDismissed(),
};

const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

export const alertStore = {
  subscribe(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  getSnapshot() {
    return state;
  },
  // Replace the whole alert list (call this from the real data source later).
  setAlerts(alerts) {
    state = { ...state, alerts: Array.isArray(alerts) ? alerts : [] };
    emit();
  },
  addAlert(alert) {
    state = { ...state, alerts: [...state.alerts, alert] };
    emit();
  },
  // Collapse the banner to the nav-pill icon.
  dismissBanner() {
    writeDismissed(true);
    state = { ...state, bannerDismissed: true };
    emit();
  },
  // Re-expand the banner (e.g. tapping the nav-pill alert icon).
  showBanner() {
    writeDismissed(false);
    state = { ...state, bannerDismissed: false };
    emit();
  },
  clearAlerts() {
    writeDismissed(false);
    state = { alerts: [], bannerDismissed: false };
    emit();
  },
  // Real free-plan storage alert. Adds the alert only for free-plan users who
  // exceed 15GB OR 5 total files; clears it otherwise. Idempotent — safe to
  // call on every subscription/usage change.
  syncStorageAlert({ isFreePlan, usedStorageGb, fileCount }) {
    const over = Boolean(isFreePlan)
      && ((Number(usedStorageGb) || 0) > FREE_STORAGE_ALERT_GB
        || (Number(fileCount) || 0) > FREE_STORAGE_ALERT_FILES);
    const has = state.alerts.some((a) => a.id === STORAGE_ALERT_ID);
    if (over && !has) {
      state = {
        ...state,
        alerts: [...state.alerts, {
          id: STORAGE_ALERT_ID,
          severity: 'warning',
          title: 'Storage limit reached',
          message: "You've hit your free plan limit. Upgrade to keep uploading videos and photos.",
        }],
      };
      emit();
    } else if (!over && has) {
      state = { ...state, alerts: state.alerts.filter((a) => a.id !== STORAGE_ALERT_ID) };
      emit();
    }
  },
};

/**
 * useAlerts — subscribe to the alert store.
 * Returns { alerts, hasAlert, showBanner, showNavIcon }.
 *   showBanner  → render the full banner (there are alerts and not dismissed)
 *   showNavIcon → render the collapsed indicator on the nav pill (dismissed)
 */
export const useAlerts = () => {
  const snapshot = useSyncExternalStore(alertStore.subscribe, alertStore.getSnapshot);
  const hasAlert = snapshot.alerts.length > 0;
  return {
    alerts: snapshot.alerts,
    hasAlert,
    showBanner: hasAlert && !snapshot.bannerDismissed,
    showNavIcon: hasAlert && snapshot.bannerDismissed,
  };
};
